import * as z from "zod";

/**
 * Keep in sync with the `GradeMapping` schema and the `GradeAcceptance`
 * enum-source registration in
 * redo/backend/db/schema/src/team/RedoManagedReturnsSettings.ts.
 */
export const GradeAcceptance = {
  ACCEPT: "accept",
  REJECT: "reject",
  FLAG_FOR_REVIEW: "flag_for_review",
} as const;
export type GradeAcceptance =
  (typeof GradeAcceptance)[keyof typeof GradeAcceptance];

export const GradeMappingSchema = z.object({
  acceptance: z.enum(GradeAcceptance),
  gradeName: z.string(),
  restock: z.boolean(),
});
export const GradeMappingsSchema = z.array(GradeMappingSchema);
export type GradeMapping = z.infer<typeof GradeMappingSchema>;

export const GRADE_ACCEPTANCE_BADGE_COLORS: Record<
  GradeAcceptance,
  "success" | "warning" | "error"
> = {
  [GradeAcceptance.ACCEPT]: "success",
  [GradeAcceptance.FLAG_FOR_REVIEW]: "warning",
  [GradeAcceptance.REJECT]: "error",
};

export const DEFAULT_GRADE_MAPPINGS: readonly GradeMapping[] = [
  { gradeName: "A", acceptance: GradeAcceptance.ACCEPT, restock: true },
  { gradeName: "B", acceptance: GradeAcceptance.ACCEPT, restock: true },
  { gradeName: "C", acceptance: GradeAcceptance.ACCEPT, restock: true },
  { gradeName: "D", acceptance: GradeAcceptance.REJECT, restock: false },
  { gradeName: "E", acceptance: GradeAcceptance.REJECT, restock: false },
];

export function findGradeMapping(
  gradeName: string,
  gradeMappings: readonly GradeMapping[],
): GradeMapping | undefined {
  return (
    gradeMappings.find((m) => m.gradeName === gradeName) ??
    DEFAULT_GRADE_MAPPINGS.find((m) => m.gradeName === gradeName)
  );
}

export function gradeRestocks(
  gradeName: string | null,
  gradeMappings: readonly GradeMapping[] = DEFAULT_GRADE_MAPPINGS,
): boolean | null {
  if (gradeName === null) {
    return null;
  }
  return findGradeMapping(gradeName, gradeMappings)?.restock ?? null;
}

export function gradeDisposition(
  gradeName: string | null,
  gradeMappings: readonly GradeMapping[] = DEFAULT_GRADE_MAPPINGS,
): "restock" | null {
  return gradeRestocks(gradeName, gradeMappings) === true ? "restock" : null;
}

export const DEFAULT_WAREHOUSE_GRADE_NAMES: readonly string[] =
  DEFAULT_GRADE_MAPPINGS.map((m) => m.gradeName);

/** Custom grades when enabled (possibly empty), else the default A–E warehouse grades. */
export function resolveWarehouseGradeNames(params: {
  customGradesEnabled: boolean;
  gradeOptions: readonly string[] | null | undefined;
}): string[] {
  if (params.customGradesEnabled) {
    return [...(params.gradeOptions ?? [])];
  }
  return [...DEFAULT_WAREHOUSE_GRADE_NAMES];
}
